import { Map as MapLibreMap } from "maplibre-gl"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { Location } from "@/reducers/locationsSlice"
import { format, isSameDay } from "date-fns"
import i18n from "@/lib/i18n/i18n"
import { getDateFnsLocale } from "@/lib/i18n/format"
import type { Leg } from "@/components/maps/RouteView"

export type LangTemplate = {
  greeting: string
  newOrderIntro: string
  totalDistance: string
  approachDistance: string
  confirmation: string
  weight: string
  // The two cargo answers read off the order (see orderCargoFormat.ts).
  palletExchange: string
  yes: string
  no: string
  temperature: string
  refNumber: string
  notes: string
  pickup: string
  delivery: string
  dropOff: string
  scale: string
  stop: string
  customs: string
  pallets: string
  hayStrawNotes?: string
  palletsCount: string
  openRouteInSlick: string
  openRouteInHereWeGo?: string
}

type SVGCanvasLayer = {
  width: number
  height: number
  data: Uint8ClampedArray
  context: CanvasRenderingContext2D
  onAdd: () => void
  render: () => boolean
}

export const svgToCanvas = (
  map: MapLibreMap,
  svgString: string,
): SVGCanvasLayer => {
  const size = 48 // Use consistent size
  let imgLoaded = false
  let img: HTMLImageElement | null = null

  return {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    context: null as unknown as CanvasRenderingContext2D,

    onAdd() {
      const canvas = document.createElement("canvas")
      canvas.width = this.width
      canvas.height = this.height
      this.context = canvas.getContext("2d") as CanvasRenderingContext2D

      // Create SVG image
      img = new Image()
      const svgBlob = new Blob([svgString], { type: "image/svg+xml" })
      const url = URL.createObjectURL(svgBlob)

      img.onload = () => {
        imgLoaded = true
        URL.revokeObjectURL(url)
        map.triggerRepaint() // Request a repaint once the image is loaded
      }

      img.src = url
    },

    render() {
      if (!imgLoaded || !img) return true // Skip rendering if image isn't loaded yet

      const context = this.context

      // Clear canvas
      context.clearRect(0, 0, this.width, this.height)

      // Draw SVG on canvas
      context.drawImage(img, 0, 0, this.width, this.height)

      // Update image data
      this.data = context.getImageData(0, 0, this.width, this.height).data

      return true
    },
  }
}

export const sumRouteMetrics = (
  route: Route2g,
): { totalDistance: number; totalDuration: number } => {
  // Validate the legs structure
  if (!route.stops || !Array.isArray(route.stops)) {
    throw new Error(`No stops found`)
  }

  // Sum up the distance and duration from all legs
  const totalDistance =
    route.stops[route.stops.length - 1].remaining_distance ?? 0
  const totalDuration = route.stops[route.stops.length - 1].remaining_time ?? 0

  return {
    totalDistance,
    totalDuration,
  }
}

/** When the traffic on the route is from, as a clock time, with the date once it is not the day of `now`. */
export const formatTrafficFetchedAt = (
  trafficFetchedAt: string | number | null | undefined,
  now: number = Date.now(),
): string | null => {
  const fetchedAt =
    typeof trafficFetchedAt === "string"
      ? Date.parse(trafficFetchedAt)
      : (trafficFetchedAt ?? NaN)
  if (Number.isNaN(fetchedAt)) return null
  return format(
    fetchedAt,
    isSameDay(fetchedAt, now) ? "HH:mm" : "d MMM HH:mm",
    { locale: getDateFnsLocale() },
  )
}

export const sumBetweenStops = (stops: RouteStop[]) => {
  if (!stops || stops.length === 0)
    return {
      totalDistance: 0,
      totalDuration: 0,
    }

  if (stops.length === 1)
    return {
      totalDistance: stops[0].remaining_distance ?? 0,
      totalDuration: stops[0].remaining_time ?? 0,
    }

  const totalDistance =
    (stops.at(-1)?.remaining_distance ?? 0) -
    (stops.at(0)?.remaining_distance ?? 0)
  const totalDuration =
    (stops.at(-1)?.remaining_time ?? 0) - (stops.at(0)?.remaining_time ?? 0)

  return {
    totalDistance,
    totalDuration,
  }
}

const getCoordinates = (location: Location) => {
  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }
  return ""
}

const getActionType = (action: string) => {
  return action.toLowerCase() === "pickup"
    ? "pickup"
    : action.toLowerCase() === "delivery"
      ? "delivery"
      : action.toLowerCase() === "drop-off"
        ? "drop-off"
        : action.toLowerCase() === "scale"
          ? "scale"
          : action.toString() === "customs"
            ? "customs"
            : "stop"
}
// Get emoji based on action type
const getActionLabel = (
  action: string | undefined,
  langTemplate: LangTemplate,
) => {
  if (!action) return langTemplate.stop
  const lowerAction = action.toLowerCase()
  if (lowerAction === "pickup") return langTemplate.pickup
  if (lowerAction === "delivery") return langTemplate.delivery
  if (lowerAction === "drop-off") return langTemplate.dropOff
  if (lowerAction === "scale") return langTemplate.scale
  if (lowerAction === "customs") return langTemplate.customs
  // "waypoint", "other", and any unknown action types all display as "Przystanek"
  return langTemplate.stop
}

// Get formatted address from location, with fallback to constructed address
const getLocationAddress = (location: Location) => {
  if (location.formattedAddress) {
    return location.formattedAddress
  }

  // Fallback: construct address from available components
  const addressParts = [
    location.street,
    location.locality,
    location.postalCode,
    location.country,
  ].filter(Boolean)

  return addressParts.length ? addressParts.join(", ") : "Unknown location"
}

const getFormattedScheduledArrivalTime = (
  scheduledArrivalTime: string | undefined,
  hourMin: string | undefined,
  hourMax: string | undefined,
): string | undefined => {
  if (!scheduledArrivalTime) return ""
  const date = format(new Date(scheduledArrivalTime), "dd-MM-yyyy")

  if (hourMin && hourMax) {
    if (hourMin !== hourMax)
      return `${date} ${hourMin.substring(0, 5)} - ${hourMax.substring(0, 5)}`
    return `${date} ${hourMin.substring(0, 5)}`
  } else if (hourMin && !hourMax) {
    return `${date} After ${hourMin.substring(0, 5)}`
  } else if (hourMax && !hourMin) {
    return `${date} Before ${hourMax.substring(0, 5)}`
  }

  return format(new Date(scheduledArrivalTime), "dd-MM-yyyy")
}

// What the driver message needs from a stop's metadata. Structurally
// satisfied by `StopMetaData`, but declared here so this module does not
// depend on the order-analysis components.
type StopSummaryMetaData = {
  action?: string
  scheduled_hour_min?: string
  scheduled_hour_max?: string
  time_fixed?: boolean
  location: {
    companyName?: string
    formattedAddress?: string
    lat?: string
    lon?: string
  }
  pallet_count?: string
  weight_kg?: string
}

// Coordinates come from the backend as strings and may be reformatted on the
// way, so they are compared numerically.
const COORDINATE_MATCH_TOLERANCE = 1e-5

const isSameCoordinate = (
  first: string | undefined,
  second: string | undefined,
) => {
  const a = Number(first)
  const b = Number(second)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false
  return Math.abs(a - b) < COORDINATE_MATCH_TOLERANCE
}

const isSameAddress = (first: string | undefined, second: string | undefined) =>
  Boolean(first?.trim()) && first?.trim() === second?.trim()

// Pairs every stop with its metadata entry.
//
// A route can hold more stops than the analyzed document described - the
// driver's unfinished stops are prepended to the ones read from the document,
// while the metadata covers only the document. Metadata stops carry no id, so
// they are matched by coordinates: a `RouteStop` built from metadata copies
// `lat`/`lon` from it verbatim. List position is not usable, stop sequences
// get renumbered.
const matchMetaDataToStops = (
  stops: RouteStop[],
  metaData: StopSummaryMetaData[] | undefined,
): (StopSummaryMetaData | undefined)[] => {
  if (!Array.isArray(metaData)) return stops.map(() => undefined)

  // One entry per stop is the normal case, and there it stays a plain 1:1
  // mapping. Matching is not used: when the dispatcher picks a Google place
  // for a stop, the stop takes that place's coordinates while the metadata
  // keeps the ones read from the document, so matching would drop the hours
  // from messages that are correct today.
  if (metaData.length === stops.length) return stops.map((_, i) => metaData[i])

  const usedMetaData = new Set<number>()
  return stops.map((stop) => {
    const matchIndex = metaData.findIndex(
      (meta, i) =>
        !usedMetaData.has(i) &&
        ((isSameCoordinate(stop.location?.lat, meta?.location?.lat) &&
          isSameCoordinate(stop.location?.lon, meta?.location?.lon)) ||
          isSameAddress(
            stop.location?.formattedAddress,
            meta?.location?.formattedAddress,
          )),
    )
    if (matchIndex === -1) return undefined
    usedMetaData.add(matchIndex)
    return metaData[matchIndex]
  })
}

// Renders the parenthesised deadline that follows the stop label.
//
// Metadata adds the hour window and the FIX marker. Without it only the date
// the stop carries itself is shown: the time inside `scheduled_arrival_time`
// is `scheduled_hour_max`, with a 23:59 sentinel for "the document gave no
// hour", so rendering it would invent a time window the dispatcher never saw.
const formatStopDeadline = (
  scheduledArrivalTime: string | undefined,
  stopMetaData: StopSummaryMetaData | undefined,
  hideStopTimes?: boolean,
  showFixedTimes?: boolean,
) => {
  const hourMin = hideStopTimes ? undefined : stopMetaData?.scheduled_hour_min
  const hourMax = hideStopTimes ? undefined : stopMetaData?.scheduled_hour_max
  const formattedTime = getFormattedScheduledArrivalTime(
    scheduledArrivalTime,
    hourMin,
    hourMax,
  )
  if (!formattedTime) return ""

  const fixSuffix =
    showFixedTimes && stopMetaData?.time_fixed && (hourMin || hourMax)
      ? " FIX"
      : ""
  return ` (${formattedTime}${fixSuffix})`
}

// Build summary string for a single stop
const buildStopSummary = (
  actionIndex: number,
  location: Location,
  scheduledArrivalTime: string | undefined,
  actionReferenceNumber: string,
  stopMetaData: StopSummaryMetaData | undefined,
  langTemplate: LangTemplate,
  action?: string,
  hideCoordinates?: boolean,
  hideStopTimes?: boolean,
  showFixedTimes?: boolean,
) => {
  const address = getLocationAddress(location)
  const coordinates = hideCoordinates ? null : getCoordinates(location)
  const label = getActionLabel(
    stopMetaData ? stopMetaData.action : action,
    langTemplate,
  )
  const deadline = formatStopDeadline(
    scheduledArrivalTime,
    stopMetaData,
    hideStopTimes,
    showFixedTimes,
  )
  // With metadata the company name keeps its own line even when empty, without
  // metadata the line is dropped. Left as it is - these messages go to drivers
  // and the tests compare them character by character.
  const companyNameLine = stopMetaData
    ? `${stopMetaData.location.companyName ?? ""}\n`
    : location.companyName
      ? `${location.companyName}\n`
      : ""

  let summary = `${label} ${actionIndex}${deadline}:\n${companyNameLine}${address}`

  if (coordinates) {
    summary += `\n${coordinates}`
  }

  if (actionReferenceNumber) {
    summary += `\nRef: ${actionReferenceNumber}`
  }

  // Add number of pallets and weight if available in metadata
  if (stopMetaData) {
    const logisticsInfo = []

    if (stopMetaData.pallet_count) {
      logisticsInfo.push(
        `${langTemplate.palletsCount}: ${stopMetaData.pallet_count}`,
      )
    }

    if (stopMetaData.weight_kg) {
      logisticsInfo.push(
        langTemplate.weight.replace("{weight}", stopMetaData.weight_kg),
      )
    }

    if (logisticsInfo.length > 0) {
      summary += `\n${logisticsInfo.join("\n")}`
    }
  }

  return summary
}

export const getTextSummary = (
  stops: RouteStop[],
  metaData: StopSummaryMetaData[] | undefined,
  langTemplate: LangTemplate,
  hideCoorditates?: boolean,
  hideStopTimes?: boolean,
  showFixedTimes?: boolean,
) => {
  if (!stops?.length) return "No stops in route"

  const stopsMetaData = matchMetaDataToStops(stops, metaData)
  // dictionary with defaults to 1
  const actionIndexes: Record<string, number> = {
    pickup: 0,
    delivery: 0,
    stop: 0,
  }

  return stops
    .map((stop, index) => {
      const {
        location,
        scheduled_arrival_time: scheduledArrivalTime,
        action,
      } = stop
      const normalizedAction = getActionType(action || "")
      const actionReferenceNumber = stop.action_reference_number || ""
      actionIndexes[normalizedAction] =
        (actionIndexes[normalizedAction] || 0) + 1

      return buildStopSummary(
        actionIndexes[normalizedAction],
        location,
        scheduledArrivalTime,
        actionReferenceNumber,
        stopsMetaData[index],
        langTemplate,
        action,
        hideCoorditates,
        hideStopTimes,
        showFixedTimes,
      )
    })
    .join("\n\n")
}

// Helper function to format time
export const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours > 0) {
    return i18n.t("maps.mapUtils.hoursMinutes", { hours, minutes })
  } else {
    return i18n.t("maps.mapUtils.minutes", { minutes })
  }
}

// Shared country flags mapping (3-letter ISO codes)
export const countryFlags: Record<string, string> = {
  POL: "🇵🇱", // Poland
  DEU: "🇩🇪", // Germany
  GBR: "🇬🇧", // United Kingdom
  FRA: "🇫🇷", // France
  ESP: "🇪🇸", // Spain
  ITA: "🇮🇹", // Italy
  NLD: "🇳🇱", // Netherlands
  BEL: "🇧🇪", // Belgium
  AUT: "🇦🇹", // Austria
  CHE: "🇨🇭", // Switzerland
  SWE: "🇸🇪", // Sweden
  NOR: "🇳🇴", // Norway
  DNK: "🇩🇰", // Denmark
  FIN: "🇫🇮", // Finland
  PRT: "🇵🇹", // Portugal
  IRL: "🇮🇪", // Ireland
  CZE: "🇨🇿", // Czech Republic
  SVK: "🇸🇰", // Slovakia
  HUN: "🇭🇺", // Hungary
  ROU: "🇷🇴", // Romania
  BGR: "🇧🇬", // Bulgaria
  HRV: "🇭🇷", // Croatia
  SVN: "🇸🇮", // Slovenia
  GRC: "🇬🇷", // Greece
  EST: "🇪🇪", // Estonia
  LVA: "🇱🇻", // Latvia
  LTU: "🇱🇹", // Lithuania
  LUX: "🇱🇺", // Luxembourg
  MLT: "🇲🇹", // Malta
  CYP: "🇨🇾", // Cyprus
  ISL: "🇮🇸", // Iceland
  MKD: "🇲🇰", // North Macedonia
  SRB: "🇷🇸", // Serbia
  MNE: "🇲🇪", // Montenegro
  ALB: "🇦🇱", // Albania
  BIH: "🇧🇦", // Bosnia and Herzegovina
  UKR: "🇺🇦", // Ukraine
  BLR: "🇧🇾", // Belarus
  MDA: "🇲🇩", // Moldova
}

// Shared country names mapping (3-letter ISO codes)
export const countryNames: Record<string, string> = {
  POL: "Polska",
  DEU: "Deutschland",
  GBR: "United Kingdom",
  FRA: "France",
  ESP: "España",
  ITA: "Italia",
  NLD: "Nederland",
  BEL: "Belgique/België",
  AUT: "Österreich",
  CHE: "Switzerland",
  SWE: "Sverige",
  NOR: "Norge",
  DNK: "Danmark",
  FIN: "Suomi",
  PRT: "Portugal",
  IRL: "Ireland",
  CZE: "Česká republika",
  SVK: "Slovensko",
  HUN: "Magyarország",
  ROU: "România",
  BGR: "България",
  HRV: "Hrvatska",
  SVN: "Slovenija",
  GRC: "Ελλάδα",
  EST: "Eesti",
  LVA: "Latvija",
  LTU: "Lietuva",
  LUX: "Luxembourg",
  MLT: "Malta",
  CYP: "Κύπρος",
  ISL: "Ísland",
  MKD: "Северна Македонија",
  SRB: "Србија",
  MNE: "Crna Gora",
  ALB: "Shqipëria",
  BIH: "Bosna i Hercegovina",
  UKR: "Україна",
  BLR: "Беларусь",
  MDA: "Moldova",
}

// Helper functions to get country info
export const getCountryFlag = (countryCode: string): string => {
  return countryFlags[countryCode] || "🏳️"
}

export const getCountryName = (countryCode: string): string => {
  return countryNames[countryCode] || countryCode
}
